import { MapComponent } from '@components/common/map/map-component.tsx';
import { ConfirmationModal } from '@components/confirmation-modal.tsx';
import { ExpandMore } from '@components/expand-more.tsx';
import { VersionUploader } from '@components/version-uploader/version-uploader.tsx';
import { Add, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  AppBar,
  Collapse,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Toolbar,
  Typography,
} from '@mui/material';
import { CombineZone } from '@sections/configure-map/combine-zone.tsx';
import { useDeckZones } from '@sections/configure-map/use-deck-zones.ts';
import { VersionList } from '@sections/configure-map/version-list/version-list.tsx';
import ZonesDetail from '@sections/configure-map/zones-detail.tsx';
import { useRef } from 'react';
import { MapRef } from 'react-map-gl/maplibre';

import { useConfigureMap } from './use-configure-map';

const tooltipStyle: React.CSSProperties = {
  position: 'absolute',
  zIndex: 1,
  pointerEvents: 'none',
};

export const ConfigureMap = () => {
  const hooks = useConfigureMap();
  const mapRef = useRef<MapRef>(null);
  const { layers, hoverInfo, focusZone } = useDeckZones(mapRef, hooks.zones);
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Grid
        container
        className="flex-1 min-h-0 overflow-hidden"
        sx={{ height: '100%' }}
      >
        <Grid
          size={{ xs: 12, sm: 6, md: 5, lg: 4, xl: 3 }}
          className="flex flex-col max-h-full border-r border-divider"
        >
          <AppBar position="relative" color="inherit" elevation={0}>
            <Toolbar variant="dense">
              <Typography sx={{ flexGrow: 1 }} component="div">
                {hooks.version ? (
                  <Typography color="primary">{hooks.version.version}</Typography>
                ) : (
                  <Typography color="secondary">
                    Seleccione una versión
                  </Typography>
                )}
              </Typography>
              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
              <ExpandMore
                expand={hooks.showDetailVersion}
                onClick={hooks.onToggleShowDetailVersion}
                aria-expanded={hooks.showDetailVersion}
                aria-label="show more"
              >
                <ExpandMoreIcon />
              </ExpandMore>
              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
              <IconButton color="primary" onClick={hooks.onStartUpload}>
                <Add />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Divider />
          {hooks.loadingZones && <LinearProgress />}
          <Collapse
            timeout="auto"
            unmountOnExit
            in={hooks.showDetailVersion}
            className="bg-white border-b border-gray-300"
          >
            <VersionList
              onChangeVersion={hooks.onChangeVersion}
              onToggleShowDetailVersion={hooks.onToggleShowDetailVersion}
            />
          </Collapse>
          <ZonesDetail
            zones={hooks.zones}
            focusZone={focusZone}
            onDeleteZone={hooks.onDeleteZone}
            onCombine={hooks.onCombine}
          />
        </Grid>
        <Grid
          size={{ xs: 12, sm: 6, md: 7, lg: 8, xl: 9 }}
          className="relative overflow-hidden"
          sx={{ height: '100%' }}
        >
          <div className="w-full h-[calc(100vh-64px)] relative">
            <MapComponent mapRef={mapRef} layers={layers}>
              {hoverInfo?.object && (
                <div
                  style={{
                    ...tooltipStyle,
                    left: hoverInfo.x,
                    top: hoverInfo.y,
                  }}
                  className="!z-10 !bg-white font-bold -translate-x-1/2 -translate-y-12 p-2 rounded-lg relative shadow-md"
                >
                  {hoverInfo.object.properties.name}
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 -translate-y-full border-8 border-transparent border-t-white"></div>
                </div>
              )}
            </MapComponent>
          </div>
        </Grid>
      </Grid>
      {hooks.forDeleteZone && (
        <ConfirmationModal
          onCancel={hooks.cancelDeleteZone}
          onSuccess={hooks.confirmDeleteZone}
        >
          <Typography>
            ¿Está seguro que desea <strong>eliminar</strong> la zona{' '}
            <strong>{hooks.forDeleteZone.name}</strong>?
            <br />
            <br />
            <strong>
              {' '}
              <small>está acción no se puede deshacer por ningún medio</small>
            </strong>
          </Typography>
        </ConfirmationModal>
      )}
      {hooks.startCombine && (
        <CombineZone
          zones={hooks.zones}
          onClose={hooks.cancelCombine}
          onSuccess={hooks.onConfirmCombine}
        />
      )}
      {hooks.startUpload && <VersionUploader onClose={hooks.onCancelUpload} />}
    </div>
  );
};
